// File: src/components/pages/students/StudentList.jsx

import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Search, Edit, Eye, Mail, Phone, AlertCircle,
  Copy, Plus, Send, X, User, CheckCircle2, Clock, Trash2
} from 'lucide-react';
import { studentAPI, invitationAPI, studentDeletionAPI } from '../../../services/api';
import { toast } from '../../../utils/toast';
import SendInvitation from '../../UserManagement/SendInvitation';
import StudentDetailsModal from '../../modals/StudentDetailsModal';
import EditStudentModal from '../../modals/EditStudentModal';

// ── Registration status badge ─────────────────────────────────────────────────
const RegBadge = ({ label, color }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
    padding: '0.2rem 0.55rem', borderRadius: '999px',
    fontSize: '0.72rem', fontWeight: 700,
    background: color + '22', color,
    whiteSpace: 'nowrap', letterSpacing: '0.02em'
  }}>
    {label === 'Invitation Expired' && <Clock size={10} />}
    {label}
  </span>
);

// ── "Add Student" popup ───────────────────────────────────────────────────────
const AddStudentModal = ({ onClose, onAdd }) => {
  const [form, setForm]     = useState({ name: '', countryCode: '+44', phoneNumber: '', email: '' });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const set = (field, value) => {
    setForm(p => ({ ...p, [field]: value }));
    if (errors[field] || errors.contact || errors.phoneNumber || errors.email) {
      setErrors(p => { const e = { ...p }; delete e[field]; delete e.contact; delete e.phoneNumber; delete e.email; return e; });
    }
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';

    const hasPhone = form.phoneNumber.trim().length > 0;
    const hasEmail = form.email.trim().length > 0;

    if (!hasPhone && !hasEmail) {
      e.contact = 'At least one of Phone or Email is required';
    } else {
      // Phone validation — minimum 10 digits
      if (hasPhone && form.phoneNumber.replace(/\D/g, '').length < 10) {
        e.phoneNumber = 'Phone number must be at least 10 digits';
      }
      // Email validation — must contain @ and .
      if (hasEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
        e.email = 'Please enter a valid email address';
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleAdd = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const phone = form.phoneNumber.trim()
        ? `${form.countryCode}${form.phoneNumber.trim()}`
        : null;
      await onAdd({ name: form.name.trim(), phone, email: form.email.trim() || null });
      onClose();
    } catch (err) {
      setErrors({ api: err.message || 'Failed to add student' });
    } finally {
      setSaving(false);
    }
  };

  const inp = (field) => ({
    width: '100%', padding: '0.55rem 0.8rem',
    border: `1.5px solid ${errors[field] || (['phone','email'].includes(field) && errors.contact) ? '#ef4444' : '#d1d5db'}`,
    borderRadius: '0.5rem', fontSize: '0.95rem',
    outline: 'none', fontFamily: 'inherit', color: '#111827',
    boxSizing: 'border-box', transition: 'border-color 0.15s', background: '#fff'
  });

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '1rem'
    }}>
      <div style={{
        background: '#fff', borderRadius: '1rem',
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        width: '100%', maxWidth: '420px', overflow: 'hidden',
        fontFamily: "'DM Sans', 'Segoe UI', sans-serif"
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
          padding: '1rem 1.25rem',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <User size={18} color="white" />
            <span style={{ color: '#fff', fontWeight: 700, fontSize: '1rem' }}>Add Student</span>
          </div>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.15)', border: 'none',
            borderRadius: '0.4rem', color: '#fff', cursor: 'pointer',
            padding: '0.3rem', display: 'flex'
          }}><X size={16} /></button>
        </div>

        <div style={{ padding: '1.25rem' }}>
          {errors.api && (
            <div style={{
              background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '0.5rem',
              padding: '0.65rem 0.8rem', color: '#991b1b', fontSize: '0.875rem', marginBottom: '1rem'
            }}>{errors.api}</div>
          )}
          {/* Name */}
          <div style={{ marginBottom: '0.85rem' }}>
            <label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#374151',
              textTransform: 'uppercase', letterSpacing: '0.04em',
              display: 'block', marginBottom: '0.3rem' }}>Name *</label>
            <input style={inp('name')} value={form.name} type="text"
              placeholder="Full name" onChange={e => set('name', e.target.value)}
              onFocus={e => { if (!errors.name) e.target.style.borderColor = '#6366f1'; }}
              onBlur={e  => { if (!errors.name) e.target.style.borderColor = '#d1d5db'; }} />
            {errors.name && <span style={{ fontSize: '0.75rem', color: '#ef4444', marginTop: '0.2rem', display: 'block' }}>{errors.name}</span>}
          </div>

          {/* Phone — country code + number */}
          <div style={{ marginBottom: '0.85rem' }}>
            <label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#374151',
              textTransform: 'uppercase', letterSpacing: '0.04em',
              display: 'block', marginBottom: '0.3rem' }}>Phone</label>
            <div style={{
              display: 'flex', borderRadius: '0.5rem', overflow: 'hidden',
              border: `1.5px solid ${errors.contact || errors.phoneNumber ? '#ef4444' : '#d1d5db'}`,
              transition: 'border-color 0.15s'
            }}
              onFocusCapture={e => { e.currentTarget.style.borderColor = (errors.contact || errors.phoneNumber) ? '#ef4444' : '#6366f1'; }}
              onBlurCapture={e  => { e.currentTarget.style.borderColor = (errors.contact || errors.phoneNumber) ? '#ef4444' : '#d1d5db'; }}
            >
              <select
                value={form.countryCode}
                onChange={e => set('countryCode', e.target.value)}
                style={{
                  padding: '0.55rem 0.4rem', fontSize: '0.9rem',
                  border: 'none', borderRight: '1.5px solid #e5e7eb',
                  background: '#f9fafb', color: '#374151', fontFamily: 'inherit',
                  outline: 'none', cursor: 'pointer', flexShrink: 0,
                  appearance: 'none', minWidth: '68px', textAlign: 'center', fontWeight: 600
                }}>
                <option value="+44">🇬🇧 +44</option>
              </select>
              <input
                type="tel"
                value={form.phoneNumber}
                onChange={e => set('phoneNumber', e.target.value.replace(/\D/g, ''))}
                placeholder="7700 900000"
                style={{
                  flex: 1, padding: '0.55rem 0.8rem', fontSize: '0.95rem',
                  border: 'none', outline: 'none', background: '#fff',
                  fontFamily: 'inherit', color: '#111827', minWidth: 0
                }}
              />
            </div>
            {errors.phoneNumber && (
              <span style={{ fontSize: '0.75rem', color: '#ef4444', marginTop: '0.2rem', display: 'block' }}>
                {errors.phoneNumber}
              </span>
            )}
          </div>

          {/* Email */}
          <div style={{ marginBottom: '0.85rem' }}>
            <label style={{ fontSize: '0.78rem', fontWeight: 700, color: '#374151',
              textTransform: 'uppercase', letterSpacing: '0.04em',
              display: 'block', marginBottom: '0.3rem' }}>Email</label>
            <input style={inp('email')} value={form.email} type="email"
              placeholder="student@example.com" onChange={e => set('email', e.target.value)}
              onFocus={e => { if (!errors.email && !errors.contact) e.target.style.borderColor = '#6366f1'; }}
              onBlur={e  => { if (!errors.email && !errors.contact) e.target.style.borderColor = '#d1d5db'; }} />
            {errors.email && (
              <span style={{ fontSize: '0.75rem', color: '#ef4444', marginTop: '0.2rem', display: 'block' }}>
                {errors.email}
              </span>
            )}
          </div>
          {errors.contact && (
            <span style={{ fontSize: '0.75rem', color: '#ef4444', display: 'block', marginBottom: '0.5rem' }}>
              {errors.contact}
            </span>
          )}
          <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: '0.25rem 0 0' }}>
            * At least one of Phone or Email is required
          </p>
        </div>

        <div style={{
          padding: '0.9rem 1.25rem', borderTop: '1px solid #f3f4f6',
          display: 'flex', justifyContent: 'flex-end', gap: '0.6rem'
        }}>
          <button onClick={onClose} disabled={saving} style={{
            padding: '0.5rem 1.1rem', borderRadius: '0.5rem',
            border: '1.5px solid #d1d5db', background: '#fff',
            color: '#374151', fontSize: '0.9rem', fontWeight: 500,
            cursor: 'pointer', fontFamily: 'inherit'
          }}>Cancel</button>
          <button onClick={handleAdd} disabled={saving} style={{
            padding: '0.5rem 1.25rem', borderRadius: '0.5rem',
            background: saving ? '#a5b4fc' : 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
            border: 'none', color: '#fff', fontSize: '0.9rem', fontWeight: 600,
            cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', gap: '0.4rem',
            boxShadow: '0 2px 8px rgba(99,102,241,0.3)'
          }}>
            {saving ? 'Adding…' : <><CheckCircle2 size={15} /> Add</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────
const StudentList = ({ status }) => {
  const { getAccessTokenSilently } = useAuth0();

  const [students, setStudents]             = useState([]);
  const [searchTerm, setSearchTerm]         = useState('');
  const [isLoading, setIsLoading]           = useState(false);
  const [error, setError]                   = useState(null);
  const [showAddModal, setShowAddModal]     = useState(false);
  const [showSendInvitation, setShowSendInvitation]               = useState(false);
  const [selectedStudentForInvitation, setSelectedStudentForInvitation] = useState(null);
  const [selectedStudentId, setSelectedStudentId]           = useState(null);
  const [showDetailsModal, setShowDetailsModal]             = useState(false);
  const [selectedStudentForEdit, setSelectedStudentForEdit] = useState(null);
  const [showEditModal, setShowEditModal]                   = useState(false);
  const [deleteTarget,  setDeleteTarget]                    = useState(null);  // student to delete
  const [isDeleting,    setIsDeleting]                      = useState(false);

  const isAddedTab = status === 'added';

  useEffect(() => { loadStudents(status); }, [status]);

  const loadStudents = async (filterStatus) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await studentAPI.getStudentsByStatus(filterStatus, getAccessTokenSilently);
      if (response.success) {
        setStudents(response.data || []);
      } else {
        setError(response.message || 'Failed to load students');
        setStudents([]);
      }
    } catch (err) {
      setError('Failed to load students. Please try again.');
      setStudents([]);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Add student (new pre-registration flow) ───────────────────────────────
  const handleAddStudent = async ({ name, phone, email }) => {
    const response = await studentAPI.addStudent({ name, phone, email }, getAccessTokenSilently);
    if (!response.success) throw new Error(response.error || 'Failed to add student');
    await loadStudents(status);
  };

  // ── Send registration email ───────────────────────────────────────────────
  // ── Open SendInvitation modal for a student ──────────────────────────────
  const openSendInvitation = (student) => {
    setSelectedStudentForInvitation(student);
    setShowSendInvitation(true);
  };

  const closeSendInvitation = () => {
    setShowSendInvitation(false);
    setSelectedStudentForInvitation(null);
  };

  // Called by SendInvitation modal's "Send Invitation" button.
  // Uses the student-specific route so the Students record is updated with
  // registrationStatus='emailSent' and invitationToken in a single atomic step.
  // Throws on duplicate (still valid) or already registered — SendInvitation modal shows the error.
  // Returns the API message so SendInvitation can display it in the success screen.
  const handleSendInvitationForStudent = async (_invitationData) => {
    const studentId = selectedStudentForInvitation?.studentId;
    if (!studentId) throw new Error('No student selected');
    const response = await invitationAPI.sendInvitationForStudent(studentId, getAccessTokenSilently);
    if (!response.success) throw new Error(response.error || 'Failed to send invitation');
    await loadStudents(status); // refresh — registrationStatus → 'emailSent', invitationToken populated
    return response.message;   // forwarded to SendInvitation's success screen
  };

  // ── Copy registration link ────────────────────────────────────────────────
  const handleCopyUrl = (student) => {
    const token = student.invitationToken;
    if (!token) { alert('No registration link yet — send the invitation first.'); return; }
    const url = `${process.env.REACT_APP_STUDENT_SIGNUP_URL}?token=${token}`;
    navigator.clipboard.writeText(url)
      .then(() => alert('Registration URL copied to clipboard!'))
      .catch(() => prompt('Copy this registration URL:', url));
  };

  const handleView  = (id)      => { setSelectedStudentId(id);    setShowDetailsModal(true); };
  const handleEdit  = (student) => { setSelectedStudentForEdit(student); setShowEditModal(true); };

  // Direct delete for 'added' tab students (no Auth0 account, no DeletionRequest needed)
  const handleDeleteAddedStudent = async (student) => {
    if (!window.confirm(
      `Delete ${student.name}?\n\n` +
      `This student has not yet registered. ` +
      `Their record will be permanently removed immediately.`
    )) return;

    try {
      const res = await studentAPI.deleteStudent(
        student.studentId || student.id,
        true,   // permanent — bypass soft-delete for unregistered students
        getAccessTokenSilently
      );
      if (res.success) {
        toast.success(`${student.name} has been removed.`);
        await loadStudents('added');
      } else {
        toast.error(res.error || 'Failed to delete student');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to delete student');
    }
  };

  const handleDeleteRequest = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      // Create a deletion request in StudentDeletionRequests on behalf of the student.
      // The instructor must then go to the Delete tab to approve it — this keeps the
      // two-step approval flow consistent with student-initiated requests.
      const res = await studentDeletionAPI.createRequestForStudent(
        deleteTarget.studentId || deleteTarget.id,
        getAccessTokenSilently
      );
      if (res.success) {
        setDeleteTarget(null);
        toast.success(
          `Deletion request created for ${deleteTarget.name}. ` +
          'Go to the Delete tab to approve and permanently remove the account.'
        );
      } else {
        toast.error(res.error || 'Failed to create deletion request');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to create deletion request');
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredStudents = students.filter(s =>
    (s.name  || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (s.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (s.phone || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ── Student-status badge (existing behaviour) ────────────────────────────
  const getStatusBadge = (st) => {
    const cfg = {
      added: { label: 'Added - 1', color: '#174536ff' },
      active: { label: 'Active - 2', color: '#10b981' },
      invited: { label: 'Invited - 3', color: '#3b82f6' },
      expired: { label: 'Link Expired - 4', color: '#3b82f6' },
      registred: { label: 'Registered - 5', color: '#10b981' },
      approve: { label: 'Pending Activation', color: '#f59e0b' },
      inactive: { label: 'Inactive', color: '#6b7280' },
      passed: { label: 'Passed', color: '#2563eb' },
      deleted: { label: 'Deleted', color: '#ef4444' }
    };
    const { label, color } = cfg[st] || cfg.active;
    return <span className="status-badge" style={{ backgroundColor: color }}>{label}</span>;
  };

  const formatDate  = (ds) => {
    if (!ds) return 'N/A';
    try { return new Date(ds).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return ds; }
  };
  const formatPhone = (p) => {
    if (!p) return 'N/A';
    if (p.startsWith('+44')) return p.replace(/(\+44)(\d{4})(\d{6})/, '$1 $2 $3');
    return p;
  };

  return (
    <div className="user-list-container">

      {/* Search + Add */}
      <div className="search-bar-with-action">
        <div className="search-bar">
          <Search size={20} className="search-icon" />
          <input type="text" placeholder="Search by name, email, or phone…"
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="search-input" />
        </div>
        <button onClick={() => setShowAddModal(true)} className="btn-new">
          <Plus size={20} /> New Student
        </button>
      </div>

      <div className="user-count">
        {filteredStudents.length} student{filteredStudents.length !== 1 ? 's' : ''} found
      </div>

      {error && <div className="error-banner"><AlertCircle size={20} /><span>{error}</span></div>}
      {isLoading && <div className="loading-state"><div className="spinner"></div><p>Loading students…</p></div>}
      {!isLoading && filteredStudents.length === 0 && !error && (
        <div className="empty-state">
          <h3>No students found</h3>
          <p>{searchTerm ? 'No students match your search.' : `No students in this tab.`}</p>
        </div>
      )}

      {!isLoading && filteredStudents.length > 0 && (
        <div className="table-container">
          <table className="user-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Contact</th>
                <th>Status</th>
                {isAddedTab && <th>Registration</th>}
                <th>Join Date</th>
                {!isAddedTab && <th>Progress</th>}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredStudents.map((student) => {
                const invId      = student.invitationId || student.studentId;
                const regStatus  = student.registrationStatus;
                const hasEmail   = student.email && student.email.includes('@');
                const hasToken   = !!student.invitationToken;

                // "Send Registration" available when email exists and student hasn't registered yet
                const canSendReg = hasEmail && ['added', 'registrationReady', 'expired'].includes(regStatus);
                // "Copy Link" available once an invitation has been sent
                const canCopy    = hasToken;

                return (
                  <tr key={student.id || student.studentId}>
                    <td>
                      <div className="user-name-cell">
                        <div className="user-avatar">
                          {(student.name || '?').charAt(0).toUpperCase()}
                        </div>
                        <span className="user-name">{student.name || 'Unknown'}</span>
                      </div>
                    </td>
                    <td>
                      <div className="contact-cell">
                        <div className="contact-item"><Mail size={14} /><span>{student.email || 'N/A'}</span></div>
                        <div className="contact-item"><Phone size={14} /><span>{formatPhone(student.phone)}</span></div>
                      </div>
                    </td>
                    <td>{getStatusBadge(student.status)}</td>

                    {isAddedTab && (
                      <td>
                        {student.registrationStatusLabel && (
                          <RegBadge
                            label={student.registrationStatusLabel}
                            color={student.registrationStatusColor || '#6b7280'}
                          />
                        )}
                      </td>
                    )}

                    <td>{formatDate(student.joinDate)}</td>

                    {!isAddedTab && (
                      <td>
                        <div className="progress-cell">
                          <div className="progress-bar">
                            <div className="progress-fill" style={{
                              width: student.lessonsTotal > 0
                                ? `${(student.lessonsCompleted / student.lessonsTotal) * 100}%` : '0%'
                            }} />
                          </div>
                          <span className="progress-text">
                            {student.lessonsCompleted}/{student.lessonsTotal} lessons
                          </span>
                        </div>
                      </td>
                    )}

                    <td>
                      <div className="action-buttons">
                        {/* 1. Copy Registration Link — only once invitation sent */}
                        {isAddedTab && (
                          <button
                            className={`action-btn view${!canCopy ? ' disabled' : ''}`}
                            onClick={() => canCopy && handleCopyUrl(student)}
                            title={canCopy ? 'Copy registration URL' : 'Send invitation first to get a link'}
                            style={{ opacity: canCopy ? 1 : 0.35, cursor: canCopy ? 'pointer' : 'default' }}
                          >
                            <Copy size={15} />
                            <span className="btn-text">Copy Link</span>
                          </button>
                        )}

                        {/* 2. Send Registration — only if email exists and not yet invited/registered */}
                        {isAddedTab && (
                          <button
                            className={`action-btn edit${!canSendReg ? ' disabled' : ''}`}
                            onClick={() => canSendReg && openSendInvitation(student)}
                            title={
                              !hasEmail   ? 'Add email to profile first' :
                              !canSendReg ? 'Already invited or registered' :
                                           'Send registration email'
                            }
                            style={{ opacity: canSendReg ? 1 : 0.35,
                                     cursor: canSendReg ? 'pointer' : 'default' }}
                          >
                            <Send size={15} />
                            <span className="btn-text">Send Reg</span>
                          </button>
                        )}

                        {/* Delete — added tab only, direct removal (no Auth0 account exists) */}
                        {isAddedTab && (
                          <button
                            className="action-btn delete"
                            onClick={() => handleDeleteAddedStudent(student)}
                            title="Delete student record"
                          >
                            <Trash2 size={15} />
                            <span className="btn-text">Delete</span>
                          </button>
                        )}

                        {/* Copy Link + Send Registration when not yet registered (any tab) */}
                        {!isAddedTab && student.registrationStatus && student.registrationStatus !== 'registered' && (
                          <>
                            <button
                              className={`action-btn view${!canCopy ? ' disabled' : ''}`}
                              onClick={() => canCopy && handleCopyUrl(student)}
                              title={canCopy ? 'Copy registration URL' : 'Send invitation first to get a link'}
                              style={{ opacity: canCopy ? 1 : 0.35, cursor: canCopy ? 'pointer' : 'default' }}
                            >
                              <Copy size={15} />
                              <span className="btn-text">Copy Link</span>
                            </button>
                            <button
                              className={`action-btn edit${!canSendReg ? ' disabled' : ''}`}
                              onClick={() => canSendReg && openSendInvitation(student)}
                              title={
                                !hasEmail   ? 'Add email to profile first' :
                                !canSendReg ? 'Already invited' :
                                             'Send registration email'
                              }
                              style={{ opacity: canSendReg ? 1 : 0.35,
                                       cursor: canSendReg ? 'pointer' : 'default' }}
                            >
                              <Send size={15} />
                              <span className="btn-text">Send Reg</span>
                            </button>
                          </>
                        )}

                        {/* 3. View Details — always shown */}
                        <button
                          className="action-btn view"
                          onClick={() => handleView(student.studentId || student.id)}
                          title="View details"
                        >
                          <Eye size={15} />
                        </button>

                        {/* 4. Edit Student — always shown */}
                        <button
                          className="action-btn edit"
                          onClick={() => handleEdit(student)}
                          title="Edit student"
                        >
                          <Edit size={15} />
                        </button>

                        {/* 5. Delete Student — active and inactive tabs */}
                        {(status === 'active' || status === 'inactive') && (
                          <button
                            className="action-btn delete"
                            onClick={() => setDeleteTarget(student)}
                            title="Request account deletion"
                          >
                            <Trash2 size={15} />
                            <span className="btn-text">Delete</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Student confirmation dialog */}
      {deleteTarget && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem'
        }}>
          <div style={{
            background: '#fff', borderRadius: '0.75rem', maxWidth: '460px', width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)', padding: '1.75rem'
          }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', marginBottom: '1rem' }}>
              <div style={{
                width: '36px', height: '36px', borderRadius: '50%',
                background: '#fef2f2', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <Trash2 size={18} color="#dc2626" />
              </div>
              <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: '#111827' }}>
                Delete Student Account
              </h3>
            </div>

            {/* Warning */}
            <div style={{
              background: '#fef2f2', border: '1px solid #fca5a5',
              borderRadius: '0.5rem', padding: '0.9rem 1rem', marginBottom: '1.25rem'
            }}>
              <p style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', color: '#7f1d1d', lineHeight: 1.6 }}>
                You are requesting deletion of the account for{' '}
                <strong>{deleteTarget.name}</strong>
                {deleteTarget.email ? ` (${deleteTarget.email})` : ''}.
              </p>
              <p style={{ margin: 0, fontSize: '0.875rem', color: '#7f1d1d', lineHeight: 1.6 }}>
                This will create a <strong>Pending</strong> deletion request. You must then go to
                the <strong>Delete</strong> tab and approve it to permanently remove the account.
                This action cannot be undone once approved.
              </p>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={isDeleting}
                style={{
                  padding: '0.55rem 1.1rem', borderRadius: '0.5rem',
                  border: '1.5px solid #e5e7eb', background: '#fff',
                  color: '#374151', fontSize: '0.875rem', fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit'
                }}>
                Cancel
              </button>
              <button
                onClick={handleDeleteRequest}
                disabled={isDeleting}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                  padding: '0.55rem 1.1rem', borderRadius: '0.5rem',
                  border: 'none', background: '#dc2626', color: '#fff',
                  fontSize: '0.875rem', fontWeight: 600,
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit', opacity: isDeleting ? 0.7 : 1
                }}>
                <Trash2 size={15} />
                {isDeleting ? 'Creating request…' : 'Create Deletion Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Invitation modal — opened by "Send Reg" button */}
      {showSendInvitation && selectedStudentForInvitation && (
        <SendInvitation
          prefilledEmail={selectedStudentForInvitation.email || ''}
          onClose={closeSendInvitation}
          onSend={handleSendInvitationForStudent}
        />
      )}

      {/* Add Student Modal */}
      {showAddModal && (
        <AddStudentModal
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddStudent}
        />
      )}

      {/* View Modal */}
      {showDetailsModal && selectedStudentId && (
        <StudentDetailsModal studentId={selectedStudentId} onClose={() => { setShowDetailsModal(false); setSelectedStudentId(null); }} />
      )}

      {/* Edit Modal */}
      {showEditModal && selectedStudentForEdit && (
        <EditStudentModal
          student={selectedStudentForEdit}
          onClose={() => { setShowEditModal(false); setSelectedStudentForEdit(null); }}
          onSuccess={() => loadStudents(status)}
        />
      )}
    </div>
  );
};

export default StudentList;